import "webroot/static/src/common/js/polyfill_ie.js";
import { LoginRepository, LoginViewData } from "../login.repository";
import { LoginOptions, RoleGroup } from "./login.service";
import { AccountInfo, SessionStore, StoredRefreshToken } from "../sessionstore";

declare var hexMD5_: (src: string) => string;

export interface LoginData {
	accounts?: AccountInfo[],
	destPage?: string,
	loginOptions: LoginOptions,
	viewData?: LoginViewData
}

export class LoginDataService {
	private sessionStore: SessionStore;
	private userId?: number;

	public loginData: LoginData;

	/*@ngInject*/
	constructor(
		private loginRepository: LoginRepository,
		private $window,
		private $location: ng.ILocationService,
		private $q: ng.IQService
	) {
		this.sessionStore = new SessionStore(loginRepository);

		this.loginData = {
			loginOptions: {}
		};
	}

	load() {
		let searchParams = new URLSearchParams(this.$window.location.search);
		this.loginData.destPage = this.$location.search().destPage || searchParams.get('destPage');

		new LoginOptionsBinder().execute(this.loginData.loginOptions);

		let userId = this.$location.search().userId || searchParams.get('userID');
		if (userId) {
			this.userId = parseInt(userId);
		}

		let promises = [];
		let viewDataReady = this.loadViewData();
		promises.push(viewDataReady);

		const accountsReady = this.loadAccounts();
		promises.push(accountsReady);

		return this.$q.all(promises);
	}

	private loadAccounts(): Promise<void> {
		if (this.loginData.accounts) {
			return Promise.resolve();
		}

		let stored = this.sessionStore.getStored();

		if (stored.length == 0) {
			this.loginData.accounts = [];
			return Promise.resolve();
		}

		return this.sessionStore.getActive()
			.then((accounts) => {
				this.loginData.accounts = this.filterAccountsByAcrRoleRestriction(accounts);

				if (this.userId) {
					// отображаем аккаунты запрошенного пользователя
					this.loginData.accounts = this.loginData.accounts.filter((s) => s.user.id === this.userId);
				}

				this.checkAccountsRefreshToken();
			});
	}

	private filterAccountsByAcrRoleRestriction(accounts: AccountInfo[]): AccountInfo[] {
		const allowedRoleGroups = this.getAllowedRoleGroupsByAcrValues();
		if (allowedRoleGroups.length === 0) {
			return accounts;
		}

		return accounts
			.map(account => {
				const userRoles = (account.userRoles || [])
					.filter(userRole => allowedRoleGroups.indexOf(userRole.role.id as RoleGroup) >= 0);
				const allowedOrganizationIds = userRoles.map(userRole => userRole.schoolId);
				const organizations = (account.organizations || [])
					.filter(organization => allowedOrganizationIds.indexOf(organization.id) >= 0);

				if (organizations.length === 0) {
					return null;
				}

				const currentOrganization = organizations.some(organization => organization.id === account.currentOrganization.id)
					? account.currentOrganization
					: organizations[0];

				return {
					...account,
					// Active token may have been issued for a role excluded by acr_values.
					// Force reauthorization with one of the remaining roles.
					active: false,
					userRoles,
					organizations,
					currentOrganization,
				};
			})
			.filter((account): account is AccountInfo => account !== null);
	}

	private getAllowedRoleGroupsByAcrValues(): RoleGroup[] {
		const acrValues = this.loginData.loginOptions.acrValues;
		if (!acrValues) {
			return [];
		}

		const roleGroupsByAcrValue: Record<string, RoleGroup> = {
			student: RoleGroup.Students,
			parent: RoleGroup.Parents,
			staff: RoleGroup.Staffs,
		};
		const allowedRoleGroups: RoleGroup[] = [];
		acrValues.split(/\s+/).forEach(pair => {
			const separatorIndex = pair.indexOf(':');
			if (separatorIndex <= 0 || pair.slice(0, separatorIndex).toLowerCase() !== 'nc_role') {
				return;
			}

			pair.slice(separatorIndex + 1).split(',').forEach(role => {
				const roleGroup = roleGroupsByAcrValue[role.toLowerCase()];
				if (roleGroup !== undefined && allowedRoleGroups.indexOf(roleGroup) < 0) {
					allowedRoleGroups.push(roleGroup);
				}
			});
		});

		return allowedRoleGroups;
	}

	private checkAccountsRefreshToken() {
		this.loginData.accounts.forEach((account) => {

			let userId = account.user.id;
			let orgId = account.currentOrganization.id;

			let storedRefreshToken = this.sessionStore.getRefreshToken(userId, orgId);

			if (!storedRefreshToken?.refreshToken) {
				return;
			}

			if (!this.validateRefreshToken(storedRefreshToken.refreshToken, account.refreshTokenHash)) {
				this.sessionStore.removeRefreshToken(userId, orgId);
			}
		});
	}

	private validateRefreshToken(refreshToken: string, refreshTokenHash: string) {
		return hexMD5_(refreshToken) == refreshTokenHash;
	}

	// todo: lazy load
	loadViewData(): Promise<void> {
		if (this.loginData.viewData) {
			return Promise.resolve();
		}

		return this.loginRepository.getLoginViewData()
			.then((viewData: LoginViewData) => {
				this.loginData.viewData = viewData;
			});
	}

	hasAccounts(): boolean {
		if(this.loginData.viewData?.esiaMainAuth) {
			this.loginData.accounts = this.loginData.accounts.filter((account) => {
				let getToken = this.sessionStore.getRefreshToken(account.user.id, account.currentOrganization.id);
				return getToken?.refreshToken ? true: false;
			})
		}

		return this.loginData.accounts && this.loginData.accounts.length > 0;
	}

	removeAccount(account: AccountInfo): void {
		let accounts = this.loginData.accounts;
		let index = accounts.findIndex(x => x.user.id == account.user.id);

		accounts.splice(index, 1);
		this.sessionStore.remove(account);
	}

	putAccount(account: AccountInfo): void {
		this.sessionStore.put(account);
	}

	storeRefreshToken(storedRefreshToken: StoredRefreshToken): void {
		this.sessionStore.setRefreshToken(storedRefreshToken);
	}

	hasRefreshToken(account: AccountInfo): boolean {
		return this.sessionStore.getRefreshToken(account.user.id, account.currentOrganization.id) != null;
	}
}

export class LoginOptionsBinder {
	execute(options: LoginOptions) {
		let searchParams = new URLSearchParams(window.location.search);
		let responseType = searchParams.get('response_type');
		if (responseType) {
			options.responseType = responseType;
		}

		let redirectUri = searchParams.get('redirect_uri');
		if (redirectUri) {
			options.redirectUri = redirectUri;
		}

		let clientId = searchParams.get('client_id');
		if (clientId) {
			options.clientId = clientId;
		}

		let scope = searchParams.get('scope');
		if (scope) {
			options.scope = scope;
		}

		let state = searchParams.get('state');
		if (state) {
			options.state = state;
		}

		let codeChallenge = searchParams.get('code_challenge');
		if (codeChallenge) {
			options.codeChallenge = codeChallenge;
		}

		let codeChallengeMethod = searchParams.get('code_challenge_method');
		if (codeChallengeMethod) {
			options.codeChallengeMethod = codeChallengeMethod;
		}

		let acrValues = searchParams.get('acr_values');
		if (acrValues) {
			options.acrValues = acrValues;
		}
	}
}
